import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

interface VerifyPageProps {}

const VerifyPage: React.FC<VerifyPageProps> = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const navigate = useNavigate();
  const { login } = useAuth();

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [expiresAt, setExpiresAt] = useState(Date.now() + 10 * 60 * 1000); // 10 minutes
  const [now, setNow] = useState(Date.now());
  const [successAnimation, setSuccessAnimation] = useState(false);

  // Update time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Redirect if no email
  useEffect(() => {
    if (!email) {
      navigate('/login');
    }
  }, [email, navigate]);

  const remainingTime = Math.max(0, expiresAt - now);
  const minutes = Math.floor(remainingTime / 60000);
  const seconds = Math.floor((remainingTime % 60000) / 1000);
  const isExpired = remainingTime === 0;

  const formatTime = (minutes: number, seconds: number): string => {
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleCodeChange = (value: string) => {
    // Only allow digits, max 6 characters
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    setError('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (code.length !== 6) {
      setError('Código deve ter 6 dígitos');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/auth/verify-otp', {
        email,
        code
      });

      if (response.data.ok) {
        // Update auth state with the user info
        login(email);

        // Trigger success animation
        setSuccessAnimation(true);

        // Wait for animation then navigate
        setTimeout(() => {
          navigate('/');
        }, 1200);
      }
    } catch (err: any) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Erro ao verificar código. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendLoading) return;

    setResendLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/auth/resend-otp', {
        email
      });

      if (response.data.ok) {
        // Reset timer and code
        setExpiresAt(Date.now() + 10 * 60 * 1000);
        setCode('');
        setError('');
      }
    } catch (err: any) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Erro ao reenviar código. Tente novamente.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-retro-bg flex items-center justify-center p-4 relative overflow-hidden">
      {/* Retro scanlines effect */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black opacity-10"></div>
        <div className="absolute inset-0" style={{
          background: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 2px, transparent 4px)'
        }}></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Main verify card */}
        <div
          className={`retro-card p-8 space-y-6 transition-all duration-300 ${
            successAnimation ? 'scale-105 shadow-retro-3' : ''
          }`}
          style={{
            background: successAnimation
              ? 'linear-gradient(135deg, var(--surface) 0%, var(--surface-2) 100%)'
              : undefined
          }}
        >
          {/* Header */}
          <div className="text-center space-y-4">
            <div className={`w-16 h-16 mx-auto bg-retro-accent rounded-retro border-2 border-retro-line flex items-center justify-center shadow-retro-1 transition-all duration-300 ${
              successAnimation ? 'animate-pulse' : ''
            }`}>
              <span className="text-2xl">🔑</span>
            </div>
            <div>
              <h1 className="text-retro-pixel text-2xl text-retro-accent text-retro-glow mb-2">
                VERIFICAÇÃO
              </h1>
              <p className="text-retro-muted text-sm break-all">
                {email}
              </p>
            </div>
          </div>

          {/* Timer */}
          <div className="text-center">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-retro border-2 transition-all duration-300 ${
              isExpired
                ? 'bg-retro-error border-retro-error text-white'
                : remainingTime < 60000
                  ? 'bg-retro-warn border-retro-warn text-black animate-pulse'
                  : 'bg-retro-surface-2 border-retro-line-soft text-retro-text'
            }`}>
              <span className="text-lg">⏰</span>
              <span className="font-mono text-lg font-bold">
                {formatTime(minutes, seconds)}
              </span>
            </div>
            <p className="text-xs text-retro-muted mt-2">
              {isExpired ? 'Código expirado' : 'Tempo restante'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-retro-text mb-2">
                Código de verificação
              </label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                className="retro-input w-full text-center text-xl tracking-widest font-mono"
                placeholder="_ _ _ _ _ _"
                autoFocus
                disabled={loading || isExpired}
              />
            </div>

            {/* Timer and resend info */}
            <div className="flex items-center justify-between text-sm">
              {isExpired ? (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendLoading}
                  className="retro-btn-secondary text-xs"
                >
                  {resendLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 border border-retro-text border-t-transparent rounded-full animate-spin"></div>
                      ENVIANDO...
                    </span>
                  ) : (
                    '🔄 REENVIAR CÓDIGO'
                  )}
                </button>
              ) : (
                <span className="text-retro-muted text-xs">
                  💡 Não compartilhe este código
                </span>
              )}
            </div>

            {error && (
              <div className="retro-status-error">
                <span>❌</span>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || code.length !== 6 || isExpired}
              className="retro-btn-primary w-full retro-press"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                  VERIFICANDO...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  ✅ VERIFICAR CÓDIGO
                </span>
              )}
            </button>
          </form>

          {/* Back button */}
          <div className="text-center">
            <button
              onClick={() => navigate('/login')}
              className="retro-btn-ghost text-sm"
              disabled={loading}
            >
              ← Voltar ao login
            </button>
          </div>
        </div>

        {/* Success animation overlay */}
        {successAnimation && (
          <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
            <div className="success-burst">
              <div className="text-6xl animate-bounce">🎮</div>
              <div className="text-retro-pixel text-2xl text-retro-primary text-retro-glow mt-4 animate-pulse">
                ACESSO LIBERADO!
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .success-burst {
          animation: successPulse 1.2s ease-out forwards;
        }

        @keyframes successPulse {
          0% {
            transform: scale(0.5);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 0.9;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .success-burst,
          .animate-pulse,
          .animate-bounce {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default VerifyPage;